
import * as React from "react";
import { cn } from "@/lib/utils/cn";

type AccordionType = "single" | "multiple";

interface AccordionContextValue {
  value: string | string[];
  onChange: (value: string) => void;
  type: AccordionType;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

interface AccordionItemContextValue {
  itemValue: string;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null);

interface AccordionProps {
  children: React.ReactNode;
  type?: AccordionType;
  defaultValue?: string | string[];
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  className?: string;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ children, type = "single", defaultValue, value: controlledValue, onValueChange, className }, ref) => {
    const [internalValue, setInternalValue] = React.useState<string | string[]>(
      defaultValue || (type === "multiple" ? [] : "")
    );

    const value = controlledValue !== undefined ? controlledValue : internalValue;

    const handleChange = (itemValue: string) => {
      let newValue: string | string[];

      if (type === "multiple") {
        const currentArray = Array.isArray(value) ? value : [];
        newValue = currentArray.includes(itemValue)
          ? currentArray.filter((v) => v !== itemValue)
          : [...currentArray, itemValue];
      } else {
        newValue = value === itemValue ? "" : itemValue;
      }

      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    return (
      <AccordionContext.Provider value={{ value, onChange: handleChange, type }}>
        <div ref={ref} className={cn("space-y-0.5", className)}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  }
);
Accordion.displayName = "Accordion";

interface AccordionItemProps {
  children: React.ReactNode;
  value: string;
  className?: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, value, className }, ref) => {
    return (
      <AccordionItemContext.Provider value={{ itemValue: value }}>
        <div ref={ref} className={cn("border-b border-border", className)}>
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  }
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className }, ref) => {
    const context = React.useContext(AccordionContext);
    const itemContext = React.useContext(AccordionItemContext);
    
    const itemValue = itemContext?.itemValue || "";

    const isOpen = React.useMemo(() => {
      if (!context || !itemValue) return false;
      return Array.isArray(context.value)
        ? context.value.includes(itemValue)
        : context.value === itemValue;
    }, [context, itemValue]);

    const handleClick = () => {
      if (itemValue && context) {
        context.onChange(itemValue);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        className={cn(
          "flex w-full items-center justify-between py-1.5 text-xs font-semibold transition-colors hover:text-primary",
          className
        )}
        aria-expanded={isOpen}
      >
        {children}
        <svg
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  }
);
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className }, ref) => {
    const context = React.useContext(AccordionContext);
    const itemContext = React.useContext(AccordionItemContext);
    
    const itemValue = itemContext?.itemValue || "";

    const isOpen = React.useMemo(() => {
      if (!context || !itemValue) return false;
      return Array.isArray(context.value)
        ? context.value.includes(itemValue)
        : context.value === itemValue;
    }, [context, itemValue]);

    if (!isOpen) return null;

    return (
      <div ref={ref} className={cn("pb-3 pt-0", className)}>
        {children}
      </div>
    );
  }
);
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
